// src/tabs/ClaimsTab.jsx
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Box,
  Chip,
  Divider,
  Skeleton,
} from "@mui/material";

import BuildIcon from "@mui/icons-material/Build";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TagIcon from "@mui/icons-material/Tag";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CancelIcon from "@mui/icons-material/Cancel";
import SmsIcon from "@mui/icons-material/Sms";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";

import { formatDateTime } from "../../../utils/helpers";
import { useOutletContext } from "react-router-dom";

/* ─────────────────────────────────────────────
   Keyframes
───────────────────────────────────────────── */
const ANIMATION_STYLES = `
  @keyframes floatBlob {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    33%  { transform: translate(30px,-30px) scale(1.05); }
    66%  { transform: translate(-20px,20px) scale(0.97); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes floatBlob2 {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    33%  { transform: translate(-25px,25px) scale(1.04); }
    66%  { transform: translate(20px,-15px) scale(0.98); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
`;

/* ─────────────────────────────────────────────
   Background blob
───────────────────────────────────────────── */
function GradientBlob({ sx }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed",
        borderRadius: "50%",
        filter: "blur(72px)",
        pointerEvents: "none",
        zIndex: 0,
        ...sx,
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Status config — colours + icon + label
───────────────────────────────────────────── */
function getStatusConfig(status) {
  switch (status) {
    case "pending":
      return {
        label: "Pending",
        icon: <HourglassTopIcon />,
        bgcolor: "#fff7ed",
        color: "#c2410c",
        border: "#fed7aa",
      };
    case "otp_sent":
      return {
        label: "OTP Sent",
        icon: <SmsIcon />,
        bgcolor: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
      };
    case "approved":
      return {
        label: "Approved",
        icon: <AssignmentTurnedInIcon />,
        bgcolor: "#f0fdf4",
        color: "#15803d",
        border: "#bbf7d0",
      };
    case "completed":
      return {
        label: "Completed",
        icon: <CheckCircleIcon />,
        bgcolor: "#f0fdf4",
        color: "#15803d",
        border: "#bbf7d0",
      };
    case "rejected":
      return {
        label: "Rejected",
        icon: <CancelIcon />,
        bgcolor: "#fef2f2",
        color: "#dc2626",
        border: "#fecaca",
      };
    default:
      return {
        label: status?.replace(/_/g, " ") ?? "Unknown",
        icon: <HourglassTopIcon />,
        bgcolor: "#f8fafc",
        color: "#475569",
        border: "#e2e8f0",
      };
  }
}

/* ─────────────────────────────────────────────
   Status Chip
───────────────────────────────────────────── */
function StatusChip({ status }) {
  const cfg = getStatusConfig(status);
  return (
    <Chip
      icon={React.cloneElement(cfg.icon, {
        sx: { fontSize: "12px !important", color: `${cfg.color} !important` },
      })}
      label={cfg.label}
      size="small"
      sx={{
        height: 22,
        fontSize: { xs: 10, sm: 11 },
        fontWeight: 700,
        letterSpacing: 0.2,
        bgcolor: cfg.bgcolor,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        borderRadius: 0.2,
        flexShrink: 0,
        "& .MuiChip-icon": { ml: "5px" },
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Claim type pill (Pickup / Drop-off)
───────────────────────────────────────────── */
function ClaimTypePill({ type }) {
  const isPickup = type === "pickup";
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {isPickup ? (
        <LocalShippingIcon sx={{ fontSize: 12, color: "#7c3aed" }} />
      ) : (
        <StorefrontIcon sx={{ fontSize: 12, color: "#0891b2" }} />
      )}
      <Typography
        sx={{
          fontSize: { xs: 10, sm: 11 },
          fontWeight: 600,
          color: isPickup ? "#7c3aed" : "#0891b2",
        }}
      >
        {isPickup ? "Pickup" : "Drop-off"}
      </Typography>
    </Stack>
  );
}

/* ─────────────────────────────────────────────
   Skeleton loader card
───────────────────────────────────────────── */
function ClaimSkeleton() {
  return (
    <Card
      elevation={0}
      sx={{ borderRadius: 0.2, border: "1px solid #e8f0fe", bgcolor: "#fff" }}
    >
      <CardContent sx={{ p: "16px !important" }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 0.2, flexShrink: 0 }} />
          <Stack flex={1} spacing={0.8}>
            <Skeleton variant="text" width="60%" height={16} />
            <Skeleton variant="text" width="40%" height={13} />
            <Skeleton variant="text" width="50%" height={13} />
          </Stack>
        </Stack>
        <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: 1.5, borderRadius: 0.2 }} />
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────────
   Single claim card
───────────────────────────────────────────── */
function ClaimCard({ cr, index, onOpenClaimDetails }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0.2,
        border: "1px solid #e8f0fe",
        bgcolor: "#fff",
        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        animation: "cardIn 0.3s ease both",
        animationDelay: `${index * 0.06}s`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "@media (hover: hover)": {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
          },
        },
      }}
    >
      <CardContent sx={{ p: { xs: "14px !important", sm: "16px !important" } }}>

        {/* ── Row 1: Device avatar + info ── */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start">

          {/* Icon tile */}
          <Box
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
              borderRadius: 0.2,
              bgcolor: "#f0fdf4",
              border: "1px solid #dcfce7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BuildIcon sx={{ color: "#16a34a", fontSize: { xs: 19, sm: 21 } }} />
          </Box>

          {/* Device name + sub info */}
          <Box flex={1} minWidth={0}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 13, sm: 14 },
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {cr.device?.brand} {cr.device?.model}
              </Typography>
              <StatusChip status={cr.status} />
            </Stack>

            {cr.device?.product_name && (
              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 11 },
                  color: "#64748b",
                  mt: 0.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cr.device.product_name}
              </Typography>
            )}
          </Box>
        </Stack>

        <Divider sx={{ my: 1.3, borderColor: "#f1f5f9" }} />

        {/* ── Row 2: Meta details ── */}
        <Stack spacing={0.8}>

          {/* Claim ID + claim type side by side */}
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={0.6} alignItems="center">
              <TagIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 600,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  Claim ID
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 700,
                    color: "#374151",
                    fontFamily: "monospace",
                  }}
                >
                  {cr.id}
                </Typography>
              </Box>
            </Stack>

            <ClaimTypePill type={cr.claim_type} />
          </Stack>

          {/* IMEI + Date/time pill */}
          <Stack
            direction="row"
            sx={{
              bgcolor: "#f8fafc",
              border: "1px solid #f1f5f9",
              borderRadius: 0.2,
              overflow: "hidden",
            }}
          >
            {/* IMEI */}
            {cr.device?.imei1 && (
              <Stack
                direction="row"
                spacing={0.6}
                alignItems="center"
                flex={1}
                sx={{ px: { xs: 1, sm: 1.2 }, py: 0.9 }}
              >
                <FingerprintIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
                <Box minWidth={0}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "#94a3b8",
                      fontWeight: 600,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                    }}
                  >
                    IMEI
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 10 },
                      fontWeight: 600,
                      color: "#374151",
                      fontFamily: "monospace",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cr.device.imei1}
                  </Typography>
                </Box>
              </Stack>
            )}

            {cr.device?.imei1 && (
              <Divider orientation="vertical" flexItem sx={{ borderColor: "#e8f0fe" }} />
            )}

            {/* Date */}
            <Stack
              direction="row"
              spacing={0.6}
              alignItems="center"
              flex={1}
              sx={{ px: { xs: 1, sm: 1.2 }, py: 0.9 }}
            >
              <AccessTimeIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              <Box minWidth={0}>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 600,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  Raised
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 9, sm: 10 },
                    fontWeight: 600,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDateTime(cr.created_at)}
                </Typography>
              </Box>
            </Stack>
          </Stack>

        </Stack>

        {/* ── Row 3: View Details CTA ── */}
        <Button
          fullWidth
          size="small"
          endIcon={<ChevronRightIcon sx={{ fontSize: "15px !important" }} />}
          onClick={() => onOpenClaimDetails?.(cr)}
          sx={{
            mt: 1.4,
            textTransform: "none",
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 700,
            borderRadius: 0.2,
            minHeight: 36,
            color: "#2563eb",
            border: "1px solid #dbeafe",
            bgcolor: "#eff6ff",
            justifyContent: "space-between",
            px: 1.5,
            "&:hover": { bgcolor: "#dbeafe", borderColor: "#bfdbfe" },
            "&:active": { bgcolor: "#bfdbfe" },
          }}
        >
          View Claim Details
        </Button>

      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────────
   Main tab
───────────────────────────────────────────── */
export default function ClaimsTab() {
  const {
    claims = [],
    claimsLoading,
    onRaiseClaim,
    onOpenClaimDetails,
  } = useOutletContext();

  /* ── Business logic — UNCHANGED ── */
  const getStatusColor = (status) => {
    switch (status) {
      case "otp_sent":   return "warning";
      case "completed":  return "success";
      case "rejected":   return "error";
      default:           return "default";
    }
  };

  return (
    <>
      <style>{ANIMATION_STYLES}</style>

      {/* ── Background blobs ── */}
      <GradientBlob
        sx={{
          width: { xs: 200, sm: 300 },
          height: { xs: 200, sm: 300 },
          top: -60,
          right: -80,
          background: "radial-gradient(circle, rgba(34,197,94,0.16) 0%, rgba(16,185,129,0.07) 70%)",
          animation: "floatBlob 20s ease-in-out infinite",
        }}
      />
      <GradientBlob
        sx={{
          width: { xs: 160, sm: 240 },
          height: { xs: 160, sm: 240 },
          bottom: 100,
          left: -70,
          background: "radial-gradient(circle, rgba(59,130,246,0.14) 0%, rgba(99,102,241,0.06) 70%)",
          animation: "floatBlob2 24s ease-in-out infinite",
        }}
      />

      {/* ── Content ── */}
      <Stack
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
          animation: "fadeSlideUp 0.4s ease both",
          overflowX: "hidden",
        }}
      >

        {/* ══════════════════════════════
            PAGE HEADER
        ══════════════════════════════ */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: 16, sm: 18 },
                color: "#111827",
                lineHeight: 1.2,
              }}
            >
              My Claims
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 10, sm: 11 }, color: "#94a3b8", mt: 0.3 }}
            >
              Track and manage your warranty claims
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
            onClick={() => onRaiseClaim?.()}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: 12, sm: 13 },
              borderRadius: 0.2,
              px: { xs: 1.5, sm: 2 },
              minHeight: 40,
              flexShrink: 0,
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
              boxShadow: "0 4px 12px rgba(22,163,74,0.30)",
              "&:hover": {
                background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                boxShadow: "0 6px 16px rgba(22,163,74,0.38)",
              },
              "&:active": {
                boxShadow: "0 2px 6px rgba(22,163,74,0.2)",
              },
              transition: "all 0.18s ease",
            }}
          >
            Raise Claim
          </Button>
        </Stack>

        {/* ══════════════════════════════
            LOADING STATE
        ══════════════════════════════ */}
        {claimsLoading && (
          <Stack spacing={1.5}>
            {[0, 1, 2].map((i) => (
              <ClaimSkeleton key={i} />
            ))}
          </Stack>
        )}

        {/* ══════════════════════════════
            EMPTY STATE
        ══════════════════════════════ */}
        {!claimsLoading && claims.length === 0 && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 0.2,
              border: "1px solid #e8f0fe",
              bgcolor: "#fff",
              boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent sx={{ py: { xs: 5, sm: 6 }, px: 2 }}>
              <Stack alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 0.2,
                    bgcolor: "#f0fdf4",
                    border: "1px solid #dcfce7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BuildIcon sx={{ color: "#16a34a", fontSize: 28 }} />
                </Box>
                <Box textAlign="center">
                  <Typography
                    sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, color: "#111827" }}
                  >
                    No claims yet
                  </Typography>
                  <Typography
                    sx={{ fontSize: { xs: 11, sm: 12 }, color: "#94a3b8", mt: 0.4 }}
                  >
                    Tap "Raise Claim" to start a new warranty request.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
                  onClick={() => onRaiseClaim?.()}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 0.2,
                    minHeight: 36,
                    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                    boxShadow: "0 3px 10px rgba(22,163,74,0.28)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                    },
                  }}
                >
                  Raise Claim
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* ══════════════════════════════
            CLAIM CARDS
        ══════════════════════════════ */}
        {!claimsLoading && claims.length > 0 && (
          <Stack spacing={1.5}>
            {claims.map((cr, index) => (
              <ClaimCard
                key={cr.id}
                cr={cr}
                index={index}
                onOpenClaimDetails={onOpenClaimDetails}
              />
            ))}
          </Stack>
        )}

      </Stack>
    </>
  );
}